import { observable, computed, action, extendObservable, toJS } from 'mobx';
import { app } from './../App';
import { user, cart, search } from './AllStores';
import {
  ECheckoutStatus,
  EShipmentMethod,
  ICheckoutOrder,
  IItemsSelectedYear
} from '../../../common/interfaces/checkout';

export class Checkout {
    @observable public checkoutStatus: ECheckoutStatus = ECheckoutStatus.None;
    @observable public orderRequestId: number | null = null;

    @observable public cartItemsSelectedYears: IItemsSelectedYear[] = [];
    @observable public cardItemsShowSelectYear: Map<string, boolean> = new Map<string, boolean>();
    @observable public cardItemsHasCorrectVIN: Map<number, boolean | null> = new Map<number, boolean | null>();

    @computed
    public get cartItemsSelectedYearsGroupsNames(): string[] {
        return this.cartItemsSelectedYears.map((g) => g.groupConcat);
    }

    @computed
    public get cartItemsSelectedYearsFlatItemIds(): number[] {
        const flat = this.cartItemsSelectedYears.filter(item => Boolean(item.year)).reduce((ids: number[], group) => {
            group.itemIds.map((item) => {
                ids.push(item);
            });

            return ids;
        }, []);
        return flat;
    }

    @computed
    public get cartItemsAllHaveYearSelected(): boolean {
        if (cart.currentCart === null) {
            return false;
        }

        const someNotSelected = cart.currentCart.item
            .filter((item) => item.quantity > 0)
            .some((item) => {
                const itemFound = this.cartItemsSelectedYearsFlatItemIds.indexOf(item.id);
                return itemFound === -1;
            });

        return !someNotSelected;
    }

    @computed
    public get cartItemsAllHaveVin(): boolean {
        if (cart.currentCart === null) {
            return false;
        }

        const someNotSelected = cart.currentCart.item
            .filter((item) => item.quantity > 0)
            .some((item) => {
              return this.cardItemsHasCorrectVIN.get(item.id) === null;
            });

        return !someNotSelected;
    }

    @computed
    public get cartItemsAllHaveCorrectVin(): boolean {
        if (cart.currentCart === null) {
            return false;
        }

        const someNotSelected = cart.currentCart.item
            .filter((item) => item.quantity > 0)
            .some((item) => {
              return this.cardItemsHasCorrectVIN.get(item.id) === false;
            });

        return !someNotSelected;
    }

    @computed
    public get isOnSelectingYears(): boolean {
        return (
            this.checkoutStatus === ECheckoutStatus.SelectingYears || this.checkoutStatus === ECheckoutStatus.YearsNotSelected
        );
    }

    @computed
    public get isOnVINMissing(): boolean {
        return this.checkoutStatus === ECheckoutStatus.VINMissing;
    }

    @computed
    public get isOnTerms(): boolean {
        return this.checkoutStatus === ECheckoutStatus.CheckoutTerms;
    }

    @computed
    public get isOnCheckout(): boolean {
        return (
            this.checkoutStatus === ECheckoutStatus.CheckoutProcessing ||
            this.checkoutStatus === ECheckoutStatus.CheckoutSuccess ||
            this.checkoutStatus === ECheckoutStatus.CheckoutError
        );
    }

    @computed
    public get isProcessingCheckout(): boolean {
        return this.checkoutStatus === ECheckoutStatus.CheckoutProcessing;
    }

    @action
    public beginCheckout = () => {
        this.cartItemsSelectedYears = [];
        this.orderRequestId = null;
        this.checkoutStatus = user.customerIsPAC ? ECheckoutStatus.SelectingYears : ECheckoutStatus.CheckoutTerms;
        search.clearSearchInput();
    };

    @action
    public moveToTerms = () => {
        this.checkoutStatus = ECheckoutStatus.CheckoutTerms;
    };

    @action
    public moveToVINMissing = () => {
        this.checkoutStatus = ECheckoutStatus.VINMissing;
    };

    @action
    public changeSelectedYears = async (
      groupConcat: string,
      itemIds: number[],
      year: number | null = null,
      vin: string | null = null
      ) => {
        const groupFound = this.cartItemsSelectedYearsGroupsNames.indexOf(groupConcat);
        if (groupFound !== -1) {
          if (year === null && vin === null) {
                this.cartItemsSelectedYears = this.cartItemsSelectedYears
                    .slice(0, groupFound)
                    .concat(this.cartItemsSelectedYears.slice(groupFound + 1));
            } else {
              this.cartItemsSelectedYears[groupFound].itemIds = [...itemIds];
              this.cartItemsSelectedYears[groupFound].year = year;
              this.cartItemsSelectedYears[groupFound].vin = vin;
            }
        } else if (year !== null || vin !== null) {
            this.cartItemsSelectedYears.push({ groupConcat, itemIds, year, vin });
        }
    };

    @action
    public acceptTerms = async (): Promise<boolean> => {
        try {
            this.checkoutStatus = ECheckoutStatus.CheckoutProcessing;

            let order: ICheckoutOrder = null;

            try {
                const service = await app.service('order');
                order = (await service.create({
                    cartId: cart.currentCart.id,
                    ...(cart.selectedPoNumber.trim().length > 0 && {
                        poNumber: cart.selectedPoNumber
                    }),
                    ...(user.customerIsPAC && {
                        selectedItemsYears: this.cartItemsSelectedYears.reduce((selectedItemYears, group) => {
                            return [
                                ...selectedItemYears,
                                ...group.itemIds.map((itemId) => ({
                                    itemId,
                                    selectedYear: group.year,
                                    vin: group.vin
                                }))
                            ];
                        }, [])
                    }),
                    shippingMethodId:
                        cart.selectedShippingMethod === 'pickup'
                            ? EShipmentMethod.Pickup
                            : cart.selectedShippingMethod === 'ltl'
                            ? EShipmentMethod.LTL
                            : cart.selectedShippingMethod === 'ltlwithliftgate'
                            ? EShipmentMethod.LTLWithLiftgate
                            : cart.selectedShippingMethod === 'other'
                            ? EShipmentMethod.Other
                            : EShipmentMethod.Delivery,
                    ...(cart.selectedShipmentMethodIncludesShipping && {
                        shippingAddressId: cart.selectedShippingAddressId
                    }),
                    ...(cart.selectedShippingMethod === 'other' && {
                        shippingMethodOtherId: cart.selectedOtherShippingTypeId,
                        shippingMethodOtherAccountId: cart.selectedOtherShippingAccountNumber,
                        shippingMethodOtherTime: cart.selectedOtherShippingTime,
                        shippingMethodOtherInstructions: cart.selectedOtherShippingInstructions
                    }),
                    ...(cart.selectedShipmentMethodIsPickup && {
                        whscode: cart.selectedPickupWarehouse.whsCode
                    }),
                    insuranceJob: cart.isInsuranceJob ? 'Y' : 'N',
                    insuranceCode: cart.selectedInsuranceJob !== null ? cart.selectedInsuranceJob.insuranceCode : null
                })) as ICheckoutOrder;
            } catch (orderCreationError) {
                this.checkoutStatus = ECheckoutStatus.CheckoutError;
                return false;
            }

            this.checkoutStatus = ECheckoutStatus.CheckoutSuccess;
            this.orderRequestId = order.id;

            // Reset search to defaults
            search.searchInputValues.makeModelYear = '';
            search.searchInputValues.partNumber = '';
            search.searchInputValues.customerItemCode = '';
            search.searchInputValues.itemGroup = [];
            search.searchInputValues.certCategory = [];
            search.searchInputValues.modified = false;
            search.searchInputValues.modifiedGroups = false;

            // Reset checkout to defaults
            this.cartItemsSelectedYears = [];

            // Reset cart to defaults
            cart.resetCartOptions();
            cart.showCart = false;

            cart.getCarts();

            return true;
        } catch (err) {
            this.checkoutStatus = ECheckoutStatus.CheckoutProcessing;
            setTimeout(() => {
                this.acceptTerms();
            }, 512);
            console.error(err);
            return err;
        }
    };

    @action
    public cancelCheckout = () => {
        this.checkoutStatus = ECheckoutStatus.None;
        this.cartItemsSelectedYears = [];
        this.orderRequestId = null;
    };

    @action
    public backToYearsSelection = () => {
        this.checkoutStatus = ECheckoutStatus.SelectingYears;
    };

    @action
    public closeCheckoutAndClearCart = () => {
        search.searchInputValues.makeModelYear = '';
        search.searchInputValues.partNumber = '';
        search.searchInputValues.customerItemCode = '';
        search.searchInputValues.itemGroup = [];
        search.searchInputValues.certCategory = [];
        search.searchInputValues.modified = false;
        search.searchInputValues.modifiedGroups = false;

        cart.showCart = false;
        cart.getCarts();

        this.checkoutStatus = ECheckoutStatus.None;
        this.cartItemsSelectedYears = [];
        this.orderRequestId = null;
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/Checkout.ts